"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useNormalizedOfferItems = void 0;
var _basketUpdated = require("@ori-events/basket-updated");
var _react = require("react");
var _logger = require("../logger");
/**
 * @returns All shades from the provided concept items.
 */
const getShadesFromConcepts = items => (items ?? []).reduce((acc, cur) => {
  acc.push(...cur.shades);
  return acc;
}, []);

/**
 * Basic concept offer data normalization. Purchases data being
 * enhanced & distributed into benefits and products collections.
 * @param offer Concept basket offer.
 * @returns Normalized offer items data for 1st & 2nd offer section.
 */
const useNormalizedOfferItems = offer => {
  (0, _logger.logDebug)(`Original data in offer ${offer.offerId}:\n\t`, offer);
  const {
    concepts,
    missingQuantity,
    offerType,
    products,
    purchases
  } = offer;

  // take all shades from the concepts present in the offer
  const conceptsProducts = getShadesFromConcepts(concepts);

  // original producs collection is merged with the optional
  // product shades from all concepts in the offer
  const rawProducts = [...products, ...conceptsProducts];
  const conceptBenefits = getShadesFromConcepts(offer.conceptBenefits);

  // same concepts merging for benefits
  const rawBenefits = [...(offer.benefits ?? []), ...conceptBenefits];

  // so far, this logic is used to distribute purchased items into
  // the products and benefits collections:
  //
  // - purchased items can't be removed from basket via the offer hints popup,
  //   just added into basket via applying the offer, so the purchased items
  //   remain constant while the dialog is open, and they can't be deselected
  //
  // - original quantity of the purchased items is taken as the minimum limit,
  //   quantity can be increased
  //
  // - when the purchased item is a product, not a benefit, it will not be listed
  //   in the products collection originally, just in purchases collection
  //
  // - benefits are always listed in benefits collection, even when purchased,
  //   then also part of the purchases collection
  //
  // - given item code can appear only in one of the collections (products, benefits),
  //   not in both, so when found in benefits, all purchased quantity is put into
  //   benefits, otherwise the purchases item is being added into products

  // benefits are not moved between benefits and purchases collections,
  // so just mark the purchased ones properly, no benefit items added here
  const defaultBenefits = rawBenefits.map(item => {
    const purchasedItem = purchases.find(({
      itemCode
    }) => itemCode === item.itemCode);
    if (!(purchasedItem?.price ?? item.purchasePrice)) {
      (0, _logger.logDebug)(`Possible problem with undefined purchasePrice for item ${item.itemCode}.`);
    }
    return {
      ...item,
      isBenefit: true,
      // purchasePrice setup solves some possible configuration issues
      purchasePrice: purchasedItem?.price ?? (item.purchasePrice || item.price),
      purchasedQuantity: purchasedItem?.quantity ?? 0,
      selectedQuantity: offerType === _basketUpdated.BasketOfferTypes.CHOOSE_AND_GET ? item.quantity : purchasedItem?.quantity ?? 0
    };
  });

  // add all purchases which are not benefits into current products,
  // as the purchased products are missing in the original products collection
  const defaultProducts = [...purchases.filter(item => !defaultBenefits.some(({
    itemCode
  }) => itemCode === item.itemCode)).map(item => ({
    ...item,
    isBenefit: false,
    purchasedQuantity: item.quantity,
    purchasePrice: item.price,
    selectedQuantity: item.quantity
  })), ...rawProducts.filter(item => !purchases.some(({
    itemCode
  }) => itemCode === item.itemCode) && !defaultBenefits.some(({
    itemCode
  }) => itemCode === item.itemCode)).map(item => ({
    ...item,
    isBenefit: false,
    purchasedQuantity: 0,
    purchasePrice: item.price,
    selectedQuantity: 0
  }))];
  const defaultOfferItems = [...defaultProducts, ...defaultBenefits];

  // One collection now instead of selections, products / benefits & availability.
  const [offerItems, setOfferItems] = (0, _react.useState)(defaultOfferItems);

  // The resulting offer items (the 1st section in displayed offer) differ per offer sub-type.
  const items1stSection = (0, _react.useMemo)(() => {
    switch (offerType) {
      case _basketUpdated.BasketOfferTypes.CHOOSE_AND_GET:
        return offerItems.filter(({
          isBenefit,
          purchasedQuantity
        }) => !isBenefit || purchasedQuantity > 0);
      case _basketUpdated.BasketOfferTypes.CHOOSE_AND_CHOOSE:
        return offerItems.filter(({
          isBenefit
        }) => !isBenefit);
      default:
        return offerItems;
    }
  }, [offerItems, offerType]);

  // Required quantity is based on current purchases.
  const requiredQuantity = (0, _react.useMemo)(() => (missingQuantity ?? 0) + items1stSection.filter(item => item.purchasedQuantity > 0).reduce((acc, cur) => acc + cur.purchasedQuantity, 0), [missingQuantity, items1stSection]);

  // Benefits collection is needed for computing discounts.
  const benefits = (0, _react.useMemo)(() => offerItems.filter(({
    isBenefit
  }) => isBenefit), [offerItems]);

  // For some types of offer hints, the second section of selectable items is rendered.
  const items2ndSection = (0, _react.useMemo)(() => offerType === _basketUpdated.BasketOfferTypes.CHOOSE_AND_CHOOSE ? benefits : [], [benefits, offerType]);
  return {
    benefits,
    items1stSection,
    offerItems,
    requiredQuantity,
    items2ndSection,
    setOfferItems
  };
};
exports.useNormalizedOfferItems = useNormalizedOfferItems;